import React from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import UrgencyBanner from '../components/UrgencyBanner';
import { contactInfo } from '../data/mock';
import { Phone, MessageCircle, MapPin, Clock, Shield, Star, CheckCircle, ArrowRight } from 'lucide-react';

const MaraboutCanada = () => {
  const cities = ["Montréal", "Québec", "Toronto", "Ottawa", "Vancouver", "Calgary", "Edmonton", "Winnipeg", "Laval", "Gatineau", "Longueuil", "Sherbrooke", "Trois-Rivières", "Chicoutimi", "Rimouski"];
  
  return (
    <div className="bg-black min-h-screen">
      <SEOHead 
        title="Marabout Canada | Retour Affectif Montréal Québec Toronto | Grand Marabout Africain"
        description="Marabout au Canada : Montréal, Québec, Toronto, Ottawa, Vancouver. Grand marabout africain spécialiste du retour affectif. Récupérez votre ex en 24-72h. Consultation confidentielle au Canada."
        keywords="marabout canada, marabout montréal, marabout québec, marabout toronto, marabout ottawa, marabout vancouver, marabout africain canada, retour affectif canada, marabout sérieux canada"
        canonical="https://retour-affectif-rapide.com/marabout-canada"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 via-transparent to-transparent" />
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <span className="inline-block px-6 py-2 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-sm font-semibold tracking-wide mb-8">
              <span className="mr-2">🇨🇦</span> Canada
            </span>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              <span className="text-amber-500">Marabout Canada</span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-5xl">Retour Affectif à Distance</span>
            </h1>
            
            <p className="text-gray-300 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              <strong className="text-amber-500">Maître Diamma</strong>, grand marabout africain, intervient partout au <strong>Canada</strong> pour vos rituels de retour affectif. 
              Montréal, Québec, Toronto, Ottawa, Vancouver et toutes les provinces canadiennes.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                Consultation WhatsApp
              </a>
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-amber-500 text-amber-500 font-bold text-lg rounded-full hover:bg-amber-500 hover:text-black transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-3" />
                {contactInfo.phone}
              </a>
            </div>
          </div>
        </section>

        {/* Cities Grid */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Marabout dans toutes les <span className="text-amber-500">villes du Canada</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {cities.map((city, index) => (
                <span key={index} className="px-4 py-2 bg-gray-900/50 border border-amber-500/20 rounded-full text-gray-300 text-sm hover:border-amber-500/50 transition-colors">
                  <MapPin className="w-3 h-3 inline mr-2 text-amber-500" />
                  Marabout {city}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-amber-500/20 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                <span className="text-amber-500">Marabout au Canada</span> : Expert Retour Affectif
              </h2>
              
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Vous cherchez un <strong className="text-white">marabout au Canada</strong> pour un retour affectif ? 
                  Notre expertise traverse l'Atlantique. Que vous soyez à <strong>Montréal, Québec, Toronto ou Vancouver</strong>, 
                  nos rituels à distance sont tout aussi efficaces pour récupérer votre ex.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Marabout Montréal</h3>
                <p>
                  <strong>Marabout Montréal</strong> : intervention pour toute la métropole québécoise. 
                  Montréal, Laval, Longueuil, Brossard, Terrebonne et toute la région métropolitaine.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Marabout Québec</h3>
                <p>
                  <strong>Marabout Québec</strong> : expert du retour affectif dans la capitale nationale. 
                  Québec, Lévis, Trois-Rivières, Sherbrooke, Chicoutimi, Rimouski.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Marabout Toronto - Ontario</h3>
                <p>
                  <strong>Marabout Toronto</strong> : rituels puissants pour le Canada anglophone. 
                  Toronto, Ottawa, Mississauga, Hamilton, London, Windsor.
                </p>
                
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl mt-6">
                  <p className="text-amber-500 font-semibold">
                    Les rituels à distance fonctionnent parfaitement pour le Canada. 
                    L'énergie spirituelle ne connaît pas de frontières !
                  </p>
                </div>
                
                <div className="grid gap-4 mt-8">
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Depuis Montréal, le rituel a fonctionné en 5 jours. Mon ex est revenu !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Mamadou, Montréal</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Meilleur marabout pour le Canada. Travail efficace malgré la distance."</p>
                    <p className="text-amber-500 mt-2 text-sm">- Julie, Québec</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="mt-12 p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-white font-bold text-xl mb-4">
                  Marabout au Canada - Consultation immédiate
                </p>
                <a
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-3" />
                  Contacter le marabout
                  <ArrowRight className="w-5 h-5 ml-3" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <FloatingWhatsApp />
      <UrgencyBanner />
    </div>
  );
};

export default MaraboutCanada;
